// V2.1A Step C — Lista SAL
import React, { useMemo, useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SAL_STATUS_LABELS, SAL_STATUS_TONE, formatEuro } from './salApi';

const SubcontractSALList = ({ sals, contractTotal, onSelect, onNewSal, canCreate }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    return (sals || [])
      .filter(s => statusFilter === 'all' || s.status === statusFilter)
      .filter(s => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (s.sal_number || '').toLowerCase().includes(q)
          || (s.description || '').toLowerCase().includes(q);
      })
      .sort((a, b) => (b.progressive_number || 0) - (a.progressive_number || 0));
  }, [sals, statusFilter, search]);

  const total = Number(contractTotal || 0);

  if (!sals || sals.length === 0) {
    return (
      <div
        className="border border-dashed border-neutral-300 rounded-lg p-8 text-center bg-white"
        data-testid="sal-empty-state"
      >
        <p className="text-neutral-600 text-sm mb-3">Nessun SAL registrato per questo Subappalto.</p>
        {canCreate && (
          <Button data-testid="sal-create-first-btn" onClick={onNewSal}>
            Crea primo SAL
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="sal-list-container">
      <div className="flex items-center gap-2 flex-wrap">
        <Input
          data-testid="sal-list-search"
          placeholder="Cerca numero / descrizione"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-52" data-testid="sal-list-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {Object.entries(SAL_STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="border border-neutral-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="sal-list-table">
          <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Numero</th>
              <th className="text-left px-3 py-2">Periodo</th>
              <th className="text-left px-3 py-2">Descrizione</th>
              <th className="text-left px-3 py-2">Stato</th>
              <th className="text-right px-3 py-2">Richiesto</th>
              <th className="text-right px-3 py-2">Approvato</th>
              <th className="text-right px-3 py-2">Ritenuta</th>
              <th className="text-right px-3 py-2">Netto</th>
              <th className="text-right px-3 py-2">Avanz.</th>
              <th className="text-left px-3 py-2">Presentato</th>
              <th className="text-left px-3 py-2">Approvato il</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(sal => {
              const pct = total > 0 ? (Number(sal.gross_approved_amount || 0) / total) * 100 : 0;
              return (
                <tr
                  key={sal.id}
                  className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                  onClick={() => onSelect(sal)}
                  data-testid={`sal-row-${sal.sal_number}`}
                >
                  <td className="px-3 py-2 font-medium">
                    {sal.sal_number}{sal.is_final_sal && <span className="ml-1 text-[10px] text-amber-700 uppercase">finale</span>}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {sal.reference_period_from || '—'}<br />
                    <span className="text-xs">{sal.reference_period_to || ''}</span>
                  </td>
                  <td className="px-3 py-2 text-neutral-700 max-w-xs truncate">{sal.description || '—'}</td>
                  <td className="px-3 py-2">
                    <Badge className={`${SAL_STATUS_TONE[sal.status]} border text-[10px]`}>
                      {SAL_STATUS_LABELS[sal.status]}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatEuro(sal.gross_requested_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatEuro(sal.gross_approved_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{formatEuro(sal.retention_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{formatEuro(sal.authorized_net_amount)}</td>
                  <td className="px-3 py-2 text-right text-neutral-600">{pct.toFixed(1)}%</td>
                  <td className="px-3 py-2 text-neutral-600 text-xs">{sal.submitted_at ? new Date(sal.submitted_at).toLocaleDateString('it-IT') : '—'}</td>
                  <td className="px-3 py-2 text-neutral-600 text-xs">{sal.approved_at ? new Date(sal.approved_at).toLocaleDateString('it-IT') : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubcontractSALList;
